import { httpClient } from '@/shared/api';
import type {
  BudgetTeacherPartialInfoDto,
  BudgetTeacherPartialRowDto,
  BudgetTeacherPickListItemDto,
} from './budgetTeacherPartialInfo.dto';

const PREFIX = '/app/budget';

/** `type` query: **0** = permanent (`budgetTeachersPermanent`), **1** = contractual (`budgetTeachersContractual`). */
export const BUDGET_TEACHER_PARTIAL_TYPE = {
  permanent: 0,
  contractual: 1,
} as const;

function unwrapPayload(raw: unknown): Record<string, unknown> | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if ('result' in o && o.result != null && typeof o.result === 'object') {
    return o.result as Record<string, unknown>;
  }
  if ('data' in o && o.data != null && typeof o.data === 'object') {
    return o.data as Record<string, unknown>;
  }
  return o;
}

function readNum(o: Record<string, unknown>, keys: string[]): number | undefined {
  for (const k of keys) {
    const v = o[k];
    if (v == null || v === '') continue;
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

function readStr(o: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'string' && v.trim() !== '') return v;
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  }
  return undefined;
}

function readIdNullable(o: Record<string, unknown>, keys: string[]): string | null {
  for (const k of keys) {
    const v = o[k];
    if (v == null) continue;
    if (typeof v === 'string' && v.trim() !== '') return v;
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  }
  return null;
}

function readTeacherLocalId(o: Record<string, unknown>): number | string | null {
  const keys = ['teacherLocalIdentifier', 'TeacherLocalIdentifier'];
  for (const k of keys) {
    const v = o[k];
    if (v == null) continue;
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim() !== '') return v;
  }
  return null;
}

function parsePickListItem(raw: unknown): BudgetTeacherPickListItemDto | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  return {
    pickListItemId: readIdNullable(o, ['pickListItemId', 'PickListItemId']),
    title: readStr(o, ['title', 'Title']) ?? null,
    pickListCode: readStr(o, ['pickListCode', 'PickListCode']) ?? null,
  };
}

function parseTeacherRow(raw: unknown): BudgetTeacherPartialRowDto | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const id = readStr(o, ['id', 'Id']);
  const name = readStr(o, ['name', 'Name', 'teacherName', 'TeacherName']);
  if (!id || !name) return null;

  const pickRaw = o.budgetTeacherPickListItems ?? o.BudgetTeacherPickListItems;
  const pickArr = Array.isArray(pickRaw) ? pickRaw : [];
  const budgetTeacherPickListItems = pickArr.map(parsePickListItem).filter((x): x is BudgetTeacherPickListItemDto => x != null);

  return {
    id,
    budgetId: readIdNullable(o, ['budgetId', 'BudgetId']),
    fundId: readStr(o, ['fundId', 'FundId']) ?? null,
    teacherLocalIdentifier: readTeacherLocalId(o),
    name,
    weeklyHoursPerm: readNum(o, ['weeklyHoursPerm', 'WeeklyHoursPerm']) ?? null,
    weeklyHoursContract: readNum(o, ['weeklyHoursContract', 'WeeklyHoursContract']) ?? null,
    startDate: readStr(o, ['startDate', 'StartDate']) ?? null,
    workStartDate: readStr(o, ['workStartDate', 'WorkStartDate']) ?? null,
    socialAssistanceLL: readNum(o, ['socialAssistanceLL', 'SocialAssistanceLL']) ?? null,
    basicSalary: readNum(o, ['basicSalary', 'BasicSalary']) ?? null,
    socialAssistanceUSD: readNum(o, ['socialAssistanceUSD', 'SocialAssistanceUSD']) ?? null,
    retroEffectLL: readNum(o, ['retroEffectLL', 'RetroEffectLL']) ?? null,
    totalSalary:
      readStr(o, ['totalSalary', 'TotalSalary']) ??
      readStr(o, ['totalSalaryDisplay', 'TotalSalaryDisplay']) ??
      null,
    totalSalaryLL: readNum(o, ['totalSalaryLL', 'TotalSalaryLL']) ?? null,
    totalSalaryUSD: readNum(o, ['totalSalaryUSD', 'TotalSalaryUSD']) ?? null,
    compensationWorkDoneAboveQuotaLL:
      readNum(o, ['compensationWorkDoneAboveQuotaLL', 'CompensationWorkDoneAboveQuotaLL']) ?? null,
    compensationWorkGreater35HoursLL:
      readNum(o, ['compensationWorkGreater35HoursLL', 'CompensationWorkGreater35HoursLL']) ?? null,
    bonuseAssistanceLL: readNum(o, ['bonuseAssistanceLL', 'BonuseAssistanceLL']) ?? null,
    additionalTasksFees: readNum(o, ['additionalTasksFees', 'AdditionalTasksFees']) ?? null,
    familyContributionLL: readNum(o, ['familyContributionLL', 'FamilyContributionLL']) ?? null,
    transportationLL: readNum(o, ['transportationLL', 'TransportationLL']) ?? null,
    nssfContributionLL:
      readNum(o, ['nssfContributionLL', 'NssfContributionLL', 'nssFContributionLL', 'NSSFContributionLL']) ??
      null,
    compensationFundLL: readNum(o, ['compensationFundLL', 'CompensationFundLL']) ?? null,
    budgetTeacherPickListItems,
  };
}

function readTeacherArray(raw: unknown): BudgetTeacherPartialRowDto[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(parseTeacherRow).filter((x): x is BudgetTeacherPartialRowDto => x != null);
}

/** Map permissive backend JSON to DTO. `requestedType` must match the `type` query (0 permanent, 1 contractual). */
export function mapBudgetTeacherPartialInfo(
  raw: unknown,
  requestedType: number
): BudgetTeacherPartialInfoDto | null {
  const o = unwrapPayload(raw);
  if (!o) return null;
  const id = readStr(o, ['id', 'Id']);
  if (!id) return null;

  const perm = readTeacherArray(o.budgetTeachersPermanent ?? o.BudgetTeachersPermanent);
  const cont = readTeacherArray(
    o.budgetTeachersContractual ??
      o.BudgetTeachersContractual ??
      o.budgetTeachersContracted ??
      o.BudgetTeachersContracted ??
      o.budgetTeachersContract ??
      o.BudgetTeachersContract ??
      []
  );

  const t = requestedType === BUDGET_TEACHER_PARTIAL_TYPE.contractual ? 1 : 0;
  /** Prefer the array that matches `type`; some budgets return the active cohort only in the other property. */
  const primary = t === 0 ? perm : cont;
  const fallback = t === 0 ? cont : perm;
  const cohortTeachers = primary.length > 0 ? primary : fallback;

  return {
    id,
    budgetTeachersPermanent: perm,
    budgetTeachersContractual: cont,
    cohortTeachers,
    budgetTeacherPartialType: t,
    exchangeRateAmount:
      readNum(o, ['exchangeRateAmount', 'ExchangeRateAmount']) ??
      readNum(o, ['exchangeRate', 'ExchangeRate']) ??
      null,
    nssfContributionPermTeachers:
      readNum(o, ['nssfContributionPermTeachers', 'NssfContributionPermTeachers', 'NSSFContributionPermTeachers']) ??
      null,
    compensationFundWeight: readNum(o, ['compensationFundWeight', 'CompensationFundWeight']) ?? null,
    year: readNum(o, ['year', 'Year']) ?? null,
  };
}

export const budgetTeacherPartialInfoApi = {
  getBudgetTeacherPartialInfo: async (budgetId: string, type: number): Promise<BudgetTeacherPartialInfoDto | null> => {
    const q = new URLSearchParams();
    q.set('type', String(type));
    const raw = await httpClient.get<unknown>(
      `${PREFIX}/${encodeURIComponent(budgetId.trim())}/budget-teacher-partial-info?${q.toString()}`
    );
    return mapBudgetTeacherPartialInfo(raw, type);
  },
};
